import { create, StateCreator, StoreApi, UseBoundStore } from 'zustand';
import { devtools, persist, PersistOptions } from 'zustand/middleware';

interface PersistConfig<T> {
  name: string;
  partialize?: (state: T) => Partial<T>;
}

// Return type when persist is used
export type StoreWithPersist<T> = UseBoundStore<StoreApi<T>> & {
  persist: {
    clearStorage: () => void;
    rehydrate: () => Promise<void>;
  };
};

export function createStore<T>(
  stateCreator: StateCreator<T>,
  options?: {
    devtools?: {
      name: string;
    };
    persist?: PersistConfig<T>;
  },
) {
  let store = stateCreator;

  const shouldPersist = !!options?.persist;
  const shouldDevtools = process.env.NODE_ENV === 'development' && !!options?.devtools;

  if (shouldPersist) {
    store = persist(store, options.persist as PersistOptions<T>) as StateCreator<T>;
  }

  if (shouldDevtools) {
    store = devtools(store, { name: options.devtools?.name }) as StateCreator<T>;
  }

  const boundStore = create<T>()(store);

  if (shouldPersist) {
    // Add `.persist` manually via casting
    return boundStore as StoreWithPersist<T>;
  }

  return boundStore;
}
